import React from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useMediaQuery, Theme } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import CartItemClass from 'src/state/cart-item';

import useViewport from 'src/hooks/use-viewport';
import useUI from 'src/hooks/use-ui';
import useCart from 'src/hooks/use-cart';
import { useCartViewedMonitor } from 'src/utils/analytics/monitors/use-cart-viewed-monitor';
import useProductDemand from 'src/components/cart/cart-dropdown/product-demand/use-product-demand';
import { useAdsEnabled } from 'src/utils/ads/hooks/use-ads-enabled';

import { CartItem } from 'src/components/cart-item';
import { CartRecommendationsCarousel } from '../cart-dropdown/cart-recommendations-carousel';
import { SponsoredOnlyCartRecommendationsCarousel } from '../cart-dropdown/sponsored-products-only-cart-recommendations-carousel';
import { CartHeader } from './header';
import { CartFooter } from './footer';
import { Empty } from './empty';
import { AppliedOffers } from './applied-offers';
import { EligibleOffers } from './eligible-offers';

const shouldShowDemandCount = (item: CartItemClass, availableProductDemandCounts: string[]): boolean =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  availableProductDemandCounts.includes(item.id ?? '');

export const CartDropdownV2 = observer(
  (): JSX.Element => {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down(`xs`));
    const { height, offset, parentOffset } = useViewport();
    const { isEmbedded, variant } = useUI();
    const { isEmpty, items } = useCart();

    useCartViewedMonitor();

    const cartItems = Object.values(items);

    const { visibleProductDemandCounts, handleProductDemandChange } = useProductDemand({
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      productIds: cartItems.map((item: CartItemClass) => item.id ?? ''),
    });

    const flags = useFlags();
    const enableAdsOnlyCarousel =
      flags['growth.ads.sponsored-products-only-in-cart-recommendation-carousel.config']?.[variant] ?? false;
    const adsEnabled = useAdsEnabled();
    const showAdsOnlyCarousel = adsEnabled && enableAdsOnlyCarousel;

    return (
      <Container
        $height={height}
        $isEmbedded={isEmbedded}
        $isMobile={isMobile}
        $offset={offset}
        $parentOffset={parentOffset}
        data-testid='cart-dropdown'
      >
        <CartHeader />

        {isEmpty ? (
          <CartContent>
            <Empty />
          </CartContent>
        ) : (
          <>
            <CartContent>
              <AppliedOffers />

              {cartItems.map((item: CartItemClass) => (
                <CartItem
                  key={item.key}
                  item={item}
                  shouldShowTopBorder
                  shouldShowDiscountBadge
                  shouldShowWeightDropdown
                  shouldShowRemoveButton
                  shouldShowDemandCount={shouldShowDemandCount(item, visibleProductDemandCounts)}
                  onProductDemandChange={handleProductDemandChange}
                />
              ))}

              <EligibleOffers />

              <CarouselContainer>
                {showAdsOnlyCarousel ? (
                  <SponsoredOnlyCartRecommendationsCarousel mt='20px' alwaysUseSmallCards />
                ) : (
                  <CartRecommendationsCarousel mt='20px' alwaysUseSmallCards />
                )}
              </CarouselContainer>
            </CartContent>

            <CartFooter />
          </>
        )}
      </Container>
    );
  }
);

const Container = styled.div`
  ${({
    $height,
    $isEmbedded,
    $isMobile,
    $offset,
    $parentOffset,
  }: {
    $height: number;
    $isEmbedded: boolean;
    $isMobile: boolean;
    $offset: number;
    $parentOffset: number;
  }) => css`
    background: #fff;
    color: #121516;
    display: flex;
    flex-direction: column;
    height: ${$isEmbedded ? `${$height - $offset}px` : `${$height}px`};
    justify-content: space-between;
    overflow: hidden;
    position: relative;
    width: ${$isMobile ? '100vw' : '520px'};

    ${$isEmbedded &&
    css`
      position: relative;
      top: ${$parentOffset + $offset}px;
    `}
  `}
`;

const CartContent = styled.div`
  background: #f0f0f0;
  flex: 1;
  overflow-y: auto;
`;

const CarouselContainer = styled.div`
  background: #fff;
  border-top: 16px solid #f0f0f0;
`;
